import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { lazy, Suspense, useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { ClerkProvider, SignedIn, SignedOut, SignIn } from "@clerk/clerk-react";
import { ConvexClientProvider } from "./components/ConvexClientProvider";
import { MemberAccessGate } from "./components/MemberAccessGate";
import { Authenticated, AuthLoading } from "convex/react";
import PostHogProvider from "./components/providers/PostHogProvider";
import Layout from "./components/Layout";
import Admin from "./pages/Admin";
import Dashboard from "./pages/Dashboard";
import Events from "./pages/Events";
import Feed from "./pages/Feed";
import AI from "./pages/AI";
import WhatsApp from "./pages/WhatsApp";
import Conference from "./pages/Conference";
import Members from "./pages/Members";
import Refer from "./pages/Refer";
import InstallApp from "./pages/InstallApp";
import NotFound from "./pages/NotFound";

const queryClient = new QueryClient();
const ConferenceBrochure = lazy(() => import("./pages/ConferenceBrochure"));

const clerkPubKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY || "pk_test_c3RpcnJpbmctY2FtZWwtNzAuY2xlcmsuYWNjb3VudHMuZGV2JA"

if (!clerkPubKey) {
  throw new Error("Missing Clerk Publishable Key");
}

// Clerk appearance to match Cool Platinum design system
const clerkAppearance = {
  variables: {
    colorPrimary: "hsl(220, 15%, 22%)",
    colorBackground: "hsl(220, 15%, 99%)",
    colorText: "hsl(220, 20%, 14%)",
    colorTextSecondary: "hsl(220, 10%, 45%)",
    colorInputBackground: "hsl(220, 15%, 99%)",
    colorInputText: "hsl(220, 20%, 14%)",
    colorDanger: "#ef4444",
    fontFamily: '"Aktiv Grotesk", system-ui, -apple-system, sans-serif',
    borderRadius: "0.375rem",
  },
  elements: {
    card: "shadow-sm border border-[hsl(220,12%,88%,0.6)] rounded-md",
    formButtonPrimary:
      "font-medium tracking-wide transition-all duration-200",
    formFieldInput: "border-[hsl(220,12%,88%,0.8)] bg-background",
    formFieldLabel: "text-sm font-medium",
    socialButtonsBlockButton:
      "border-[hsl(220,12%,88%,0.8)] hover:bg-secondary transition-colors duration-150",
    footerActionLink: "font-medium hover:opacity-70",
    headerTitle: "text-xl font-semibold tracking-refined",
    headerSubtitle: "text-sm tracking-wide",
    dividerLine: "bg-[hsl(220,12%,88%)]",
    dividerText: "text-[hsl(220,10%,55%)] text-sm",
    userButtonAvatarBox: "h-9 w-9 ring-1 ring-sidebar-foreground/10",
    userButtonTrigger: "focus:shadow-none",
  },
};

// Override Clerk's default "Sign in to AlexAI Pro" to match this app's branding
const clerkLocalization = {
  signIn: {
    start: {
      title: "Sign in to continue",
      subtitle: "Welcome back to the ETP Executive Network",
    },
  },
};

const clerkOptions = {
  publishableKey: clerkPubKey,
  appearance: clerkAppearance,
  localization: clerkLocalization,
  afterSignOutUrl: import.meta.env.VITE_CLERK_AFTER_SIGN_OUT_URL ?? "/",
};

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    requestAnimationFrame(() => {
      document.querySelector("main")?.scrollTo(0, 0);
      window.scrollTo(0, 0);
    });
  }, [pathname]);
  return null;
}

const App = () => (
  <ClerkProvider {...clerkOptions}>
    <ConvexClientProvider>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
        <Toaster />
        <Sonner />
        <SignedOut>
            <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-background via-background to-muted/30 px-4">
              <div className="mb-8 text-center animate-fade-in">
                <div className="flex items-center justify-center gap-2.5 mb-2">
                  <img
                    src="/etp-icon.svg"
                    alt="ETP Executive Network"
                    className="h-9 w-9"
                  />
                  <h1 className="text-2xl md:text-3xl font-semibold text-foreground tracking-refined">
                    ETP Executive Network
                  </h1>
                </div>
                <p className="text-xs uppercase tracking-elegant text-muted-foreground font-medium">
                  Member Portal
                </p>
              </div>
              <div className="animate-fade-in-up">
                <SignIn />
              </div>
            </div>
        </SignedOut>
        <SignedIn>
          <AuthLoading>
            <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">
              Securing your member session…
            </div>
          </AuthLoading>
          <Authenticated>
            <MemberAccessGate>
              <BrowserRouter>
                <PostHogProvider>
                  <ScrollToTop />
                  <Layout>
                    <Routes>
                      <Route path="/" element={<Dashboard />} />
                      <Route path="/events" element={<Events />} />
                      <Route path="/feed" element={<Feed />} />
                      <Route path="/ai" element={<AI />} />
                      <Route path="/whatsapp" element={<WhatsApp />} />
                      <Route path="/conference" element={<Conference />} />
                      <Route
                        path="/conference/brochure"
                        element={
                          <Suspense
                            fallback={
                              <div className="flex min-h-[60vh] items-center justify-center text-sm text-muted-foreground">
                                Loading brochure…
                              </div>
                            }
                          >
                            <ConferenceBrochure />
                          </Suspense>
                        }
                      />
                      <Route path="/members" element={<Members />} />
                      <Route path="/refer" element={<Refer />} />
                      <Route path="/install-app" element={<InstallApp />} />
                      <Route path="/admin" element={<Admin />} />
                      {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </Layout>
                </PostHogProvider>
              </BrowserRouter>
            </MemberAccessGate>
          </Authenticated>
        </SignedIn>
        </TooltipProvider>
      </QueryClientProvider>
    </ConvexClientProvider>
  </ClerkProvider>
);

export default App;
